import React, { useState, useEffect, useRef } from "react";

export const getBudgetStatus = (budget, spent) => {
  if (!budget || budget === 0) return null;

  const percentage = (spent / budget) * 100;

  if (percentage >= 100) {
    return {
      status: "Over",
      color: "bg-red-100 text-red-800",
      percentage: Math.round(percentage),
    };
  } else if (percentage >= 80) {
    return {
      status: "Warning",
      color: "bg-yellow-100 text-yellow-800",
      percentage: Math.round(percentage),
    };
  } else {
    return {
      status: "OK",
      color: "bg-green-100 text-green-800",
      percentage: Math.round(percentage),
    };
  }
};

const statusIcons = {
  OK: (
    <svg className="h-3 w-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  ),
  Warning: (
    <svg className="h-3 w-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
        clipRule="evenodd"
      />
    </svg>
  ),
  Over: (
    <svg className="h-3 w-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
        clipRule="evenodd"
      />
    </svg>
  ),
};

const progressBarColors = {
  OK: "bg-green-500",
  Warning: "bg-yellow-500",
  Over: "bg-red-500",
};

const AnimatedPercentage = ({ value, className = "" }) => {
  const [displayValue, setDisplayValue] = useState(value || 0);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationRef = useRef(null);

  useEffect(() => {
    if (value === displayValue) return;

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    setIsAnimating(true);
    const startValue = displayValue;
    const endValue = value || 0;
    const duration = 500;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.round(
        startValue + (endValue - startValue) * easeOut
      );

      setDisplayValue(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(endValue);
        setIsAnimating(false);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, displayValue]);

  return (
    <span
      className={`inline-block transition-all duration-300 ${isAnimating ? "scale-110 font-bold" : "scale-100"} ${className}`}
    >
      {displayValue}
    </span>
  );
};

export const AnimatedBudgetBadge = ({
  status,
  className = "",
  title = "",
  size = "md",
}) => {
  const [prevStatus, setPrevStatus] = useState(status);
  const [isChanging, setIsChanging] = useState(false);

  useEffect(() => {
    if (status && prevStatus && status.status !== prevStatus.status) {
      setIsChanging(true);
      const timer = setTimeout(() => {
        setIsChanging(false);
      }, 600);
      return () => clearTimeout(timer);
    }
    if (status) {
      setPrevStatus(status);
    }
  }, [status, prevStatus]);

  if (!status) return null;

  const isSmall = size === "sm";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium transition-all duration-500 ${status.color} ${isChanging ? "ring-2 ring-slate-400 ring-offset-1" : ""} ${isSmall ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs"} ${className}`}
      title={title}
    >
      {statusIcons[status.status]}
      <span>{status.status}</span>
      <AnimatedPercentage value={status.percentage} />
      <span>%</span>
      <span
        className={`inline-block rounded-full bg-black/10 overflow-hidden ${isSmall ? "w-4 h-0.5" : "w-6 h-1"}`}
      >
        <span
          className={`block h-full rounded-full transition-all duration-500 ${progressBarColors[status.status]}`}
          style={{ width: `${Math.min(100, status.percentage)}%` }}
        />
      </span>
    </span>
  );
};

export default AnimatedBudgetBadge;
